import React, { useEffect, useState } from 'react'

import { Box, Button, Icon, Tag } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import PanesHeader from 'modules/core/components/PanesHeader'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import invoices from 'modules/billing/store/collections/invoices'
import type InvoiceModel from 'modules/billing/store/models/Invoice'
import type { Status } from 'modules/billing/store/models/Invoice'

import styles from './index.module.scss'

interface Props {}

const getStatusStyle = (status: Status) => {
  switch (status) {
    case 'paid':
    case 'refunded':
      return 'lime'
    case 'payment_due':
    case 'refund_due':
    case 'not_paid':
      return 'radical'
    case 'adjusted':
      return 'champagne'
    case 'posted':
      return 'purple'
    default:
      return 'radical'
  }
}

const getStatusTranslation = (status: Status): string => {
  switch (status) {
    case 'paid':
      return i18n.t('billing.invoices.status.paid')
    case 'refunded':
      return i18n.t('billing.invoices.status.refunded')
    case 'payment_due':
      return i18n.t('billing.invoices.status.payment_due')
    case 'refund_due':
      return i18n.t('billing.invoices.status.refund_due')
    case 'not_paid':
      return i18n.t('billing.invoices.status.not_paid')
    case 'adjusted':
      return i18n.t('billing.invoices.status.adjusted')
    case 'posted':
      return i18n.t('billing.invoices.status.posted')
    default:
      return status
  }
}

const InvoiceList: React.FC<Props> = () => {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchInvoices = async () => {
      await invoices.fetch()
      setLoading(false)
    }
    fetchInvoices()
  }, [])

  const renderInvoice = (invoice: InvoiceModel) => {
    const isCreditNote = invoice.attributes.credit_note

    const href = isCreditNote
      ? buildUrl(`${invoice.url()}.pdf`).replace('/invoices/', '/credit_notes/')
      : buildUrl(`${invoice.url()}.pdf`)

    const date = dateToString(moment.unix(invoice.attributes.date), 'backend')

    const startDate =
      invoice.attributes.start_date &&
      dateToString(moment.unix(invoice.attributes.start_date), 'backend')
    const endDate =
      invoice.attributes.end_date &&
      dateToString(moment.unix(invoice.attributes.end_date), 'backend')

    const tail = `${date}-${invoice.attributes.number}`

    const status = getStatusTranslation(invoice.attributes.status)
    const statusStyle = getStatusStyle(invoice.attributes.status)

    const paymentLink = invoice.attributes.payment_link

    const filename = isCreditNote
      ? i18n.t('billing.invoices.credit_note', { tail })
      : i18n.t('billing.invoices.filename', { tail })

    return (
      <li key={filename} className={styles.item}>
        <div className={styles.mimetype}>
          <Icon.Large icon="document-approved" />
        </div>
        <div className={styles.details}>
          <div className={styles.filename}>{filename}</div>
          <div className={styles.createdAt}>
            {startDate}
            {endDate ? ` - ${endDate}` : ''}
          </div>
          <Tag leftItem="circle" type={statusStyle} inline showCircle>
            {status}
          </Tag>
        </div>
        <div className={styles.linksWrapper}>
          {paymentLink && (
            <Button.Router
              aria-label="Pay invoice"
              to={paymentLink}
              rel="nofollow noopener noreferrer"
              type="secondary"
              blankTarget
              iconName="payroll"
            />
          )}
          <Button.Router
            to={href}
            rel="nofollow noopener noreferrer"
            download={filename}
            type="secondary"
            blankTarget
            iconName="download"
          />
        </div>
      </li>
    )
  }

  if (loading) return <Loading />

  if (invoices.models.length === 0) return null

  return (
    <Box>
      <PanesHeader
        illustration="drawer-document"
        title={i18n.t('settings.plans.invoices')}
        description={i18n.t('settings.plans.invoices_description')}
      />
      <ul className={styles.list}>{invoices.map(renderInvoice)}</ul>
    </Box>
  )
}

export default observer(InvoiceList)
